import type { FormField, FormSchema } from "../../studio/schemas/form-schema.js";
import { FieldType } from "../../studio/schemas/form-schema.js";

const template = document.createElement("template");
template.innerHTML = `
  <div style="display: flex; height: 100%; width: 100%;">
    <!-- Toolbox (Left Panel) -->
    <aside style="width: 250px; background: var(--color-surface-primary); border-right: 1px solid var(--color-graphite-300); padding: var(--space-4); overflow-y: auto;">
      <h3 style="font-size: var(--font-size-sm); color: var(--color-text-secondary); text-transform: uppercase; margin-bottom: var(--space-3);">Components</h3>
      <div id="toolbox" style="display: flex; flex-direction: column; gap: var(--space-2);">
        <!-- Draggable items will be injected here -->
      </div>
    </aside>

    <!-- Canvas (Center) -->
    <main style="flex: 1; padding: var(--space-6); overflow-y: auto; display: flex; justify-content: center; background: var(--color-graphite-50);">
      <div id="canvas" style="width: 100%; max-width: 800px; background: var(--color-surface-primary); min-height: 100%; border: 1px solid var(--color-graphite-300); border-radius: var(--radius-md); padding: var(--space-8); box-shadow: var(--shadow-sm);">
        <div id="dropzone" style="min-height: 200px; border: 2px dashed transparent; padding-bottom: 50px;">
          <!-- Rendered form fields go here -->
          <div id="empty-canvas" style="color: var(--color-graphite-400); text-align: center; margin-top: 50px;">
            Drag and drop components here
          </div>
        </div>
      </div>
    </main>

    <!-- Properties (Right Panel) -->
    <aside style="width: 300px; background: var(--color-surface-primary); border-left: 1px solid var(--color-graphite-300); padding: var(--space-4); overflow-y: auto;">
      <h3 style="font-size: var(--font-size-sm); color: var(--color-text-secondary); text-transform: uppercase; margin-bottom: var(--space-3);">Properties</h3>
      <div id="properties-panel">
        <p style="color: var(--color-graphite-400); font-size: var(--font-size-sm);">Select a component to edit properties.</p>
      </div>
    </aside>
  </div>
`;

export class FormBuilder extends HTMLElement {
  private schema: FormSchema = { fields: [] };
  private selectedFieldId: string | null = null;
  private onSchemaChangeCallback: ((schema: FormSchema) => void) | null = null;

  constructor() {
    super();
    this.appendChild(template.content.cloneNode(true));
  }

  public connectedCallback() {
    this.renderToolbox();
    this.setupDragAndDrop();
    this.renderCanvas();
  }

  public setSchema(schema: FormSchema) {
    this.schema = JSON.parse(JSON.stringify(schema)); // Clone
    if (!this.schema.fields) {
      this.schema.fields = [];
    }
    this.renderCanvas();
    this.renderProperties();
  }

  public onSchemaChange(callback: (schema: FormSchema) => void) {
    this.onSchemaChangeCallback = callback;
  }

  private notifyChange() {
    if (this.onSchemaChangeCallback) {
      this.onSchemaChangeCallback(JSON.parse(JSON.stringify(this.schema)));
    }
  }

  private renderToolbox() {
    const toolbox = this.querySelector("#toolbox")!;
    const fields = [
      { type: FieldType.Text, label: "Text Field", icon: "type" },
      { type: FieldType.Textarea, label: "Textarea", icon: "align-left" },
      { type: FieldType.Number, label: "Number", icon: "hash" },
      { type: FieldType.Date, label: "Date", icon: "calendar" },
      { type: FieldType.Select, label: "Select", icon: "list" },
      { type: FieldType.Checkbox, label: "Checkbox", icon: "check-square" },
      { type: FieldType.Section, label: "Section", icon: "layout" },
    ];

    fields.forEach(f => {
      const el = document.createElement("div");
      el.draggable = true;
      el.dataset['type'] = f.type;
      el.dataset['label'] = f.label;
      el.style.cssText = "padding: var(--space-3); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-md); text-align: left; color: var(--color-text-muted); font-size: var(--font-size-sm); cursor: grab; background: var(--color-surface-primary);";
      el.innerHTML = `<i data-lucide="${f.icon}" style="width: 16px; height: 16px; vertical-align: middle; margin-right: var(--space-2);"></i> ${f.label}`;
      
      el.addEventListener("dragstart", (e) => {
        e.dataTransfer!.setData("text/plain", JSON.stringify({ type: f.type, label: f.label }));
        e.dataTransfer!.effectAllowed = "copy";
      });

      toolbox.appendChild(el);
    });
  }

  private setupDragAndDrop() {
    const dropzone = this.querySelector("#dropzone") as HTMLElement;
    
    dropzone.addEventListener("dragover", (e) => {
      e.preventDefault();
      e.dataTransfer!.dropEffect = "copy";
      dropzone.style.borderColor = "var(--color-primary)";
    });

    dropzone.addEventListener("dragleave", () => {
      dropzone.style.borderColor = "transparent";
    });

    dropzone.addEventListener("drop", (e) => {
      e.preventDefault();
      dropzone.style.borderColor = "transparent";
      
      const dataStr = e.dataTransfer!.getData("text/plain");
      if (!dataStr) return;
      
      try {
        const data = JSON.parse(dataStr);
        if (data['type']) {
          const newField: FormField = {
            id: crypto.randomUUID(),
            code: "field_" + Date.now().toString().slice(-6),
            type: data['type'],
            label: data['label'],
            required: false,
            readonly: false,
            hidden: false,
            order: this.schema.fields.length,
            width: "100%"
          };
          this.schema.fields.push(newField);
          this.selectedFieldId = newField.id;
          this.renderCanvas();
          this.renderProperties();
          this.notifyChange();
        }
      } catch (err) {
        console.error("Invalid drop data", err);
      }
    });
  }

  private renderCanvas() {
    const dropzone = this.querySelector("#dropzone")!;
    dropzone.innerHTML = "";
    
    if (this.schema.fields.length === 0) {
      dropzone.innerHTML = `<div id="empty-canvas" style="color: var(--color-graphite-400); text-align: center; margin-top: 50px;">Drag and drop components here</div>`;
      return;
    }

    this.schema.fields.forEach(field => {
      const fieldEl = document.createElement("div");
      const isSelected = this.selectedFieldId === field.id;
      
      fieldEl.style.cssText = `
        padding: var(--space-3);
        margin-bottom: var(--space-3);
        border: 2px solid ${isSelected ? 'var(--color-primary)' : 'transparent'};
        border-radius: var(--radius-md);
        background: ${isSelected ? 'rgba(59, 130, 246, 0.05)' : 'white'};
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        box-shadow: ${isSelected ? 'none' : '0 1px 2px rgba(0,0,0,0.05)'};
      `;
      if (!isSelected) {
        fieldEl.style.border = "1px solid var(--color-graphite-300)";
      }

      fieldEl.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <strong style="font-size: var(--font-size-sm); color: var(--color-text-muted);">${field.label}</strong>
          <span style="font-size: var(--font-size-xs); color: var(--color-text-secondary); background: var(--color-graphite-100); padding: 2px 6px; border-radius: 4px;">${field.type}</span>
        </div>
        <div style="font-size: var(--font-size-xs); color: var(--color-text-secondary);">
          code: ${field.code} | width: ${field.width} ${field.required ? '| required' : ''}
        </div>
      `;

      fieldEl.addEventListener("click", (e) => {
        e.stopPropagation();
        this.selectedFieldId = field.id;
        this.renderCanvas();
        this.renderProperties();
      });

      dropzone.appendChild(fieldEl);
    });
  }

  private renderProperties() {
    const panel = this.querySelector("#properties-panel")!;
    
    if (!this.selectedFieldId) {
      panel.innerHTML = `<p style="color: var(--color-graphite-400); font-size: var(--font-size-sm);">Select a component to edit properties.</p>`;
      return;
    }

    const field = this.schema.fields.find(f => f.id === this.selectedFieldId);
    if (!field) return;

    panel.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: var(--space-4);">
        <div>
          <label style="display: block; font-size: var(--font-size-xs); color: var(--color-text-secondary); margin-bottom: var(--space-1);">Label</label>
          <input type="text" id="prop-label" value="${field.label}" style="width: 100%; padding: var(--space-2); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-sm); font-size: var(--font-size-sm);">
        </div>
        <div>
          <label style="display: block; font-size: var(--font-size-xs); color: var(--color-text-secondary); margin-bottom: var(--space-1);">Code (Technical ID)</label>
          <input type="text" id="prop-code" value="${field.code}" style="width: 100%; padding: var(--space-2); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-sm); font-size: var(--font-size-sm); font-family: monospace;">
        </div>
        <div>
          <label style="display: block; font-size: var(--font-size-xs); color: var(--color-text-secondary); margin-bottom: var(--space-1);">Grid Width</label>
          <select id="prop-width" style="width: 100%; padding: var(--space-2); border: 1px solid var(--color-graphite-300); border-radius: var(--radius-sm); font-size: var(--font-size-sm);">
            <option value="25%" ${field.width === '25%' ? 'selected' : ''}>25%</option>
            <option value="33%" ${field.width === '33%' ? 'selected' : ''}>33%</option>
            <option value="50%" ${field.width === '50%' ? 'selected' : ''}>50%</option>
            <option value="66%" ${field.width === '66%' ? 'selected' : ''}>66%</option>
            <option value="75%" ${field.width === '75%' ? 'selected' : ''}>75%</option>
            <option value="100%" ${field.width === '100%' ? 'selected' : ''}>100%</option>
          </select>
        </div>
        <div style="display: flex; gap: var(--space-2); align-items: center;">
          <input type="checkbox" id="prop-required" ${field.required ? 'checked' : ''}>
          <label for="prop-required" style="font-size: var(--font-size-sm); color: var(--color-text-muted);">Required</label>
        </div>
        <div style="display: flex; gap: var(--space-2); align-items: center;">
          <input type="checkbox" id="prop-readonly" ${field.readonly ? 'checked' : ''}>
          <label for="prop-readonly" style="font-size: var(--font-size-sm); color: var(--color-text-muted);">Readonly</label>
        </div>
        
        <button id="prop-delete" style="margin-top: var(--space-4); background: var(--color-surface-primary); color: var(--color-danger); border: 1px solid var(--color-danger); padding: var(--space-2); border-radius: var(--radius-md); cursor: pointer; font-size: var(--font-size-sm);">Delete Component</button>
      </div>
    `;

    // Bind events
    const updateField = (key: keyof FormField, value: any) => {
      const f = this.schema.fields.find(f => f.id === this.selectedFieldId);
      if (f) {
        (f as any)[key] = value;
        this.renderCanvas();
        this.notifyChange();
      }
    };

    panel.querySelector("#prop-label")?.addEventListener("input", (e) => updateField('label', (e.target as HTMLInputElement).value));
    panel.querySelector("#prop-code")?.addEventListener("input", (e) => updateField('code', (e.target as HTMLInputElement).value));
    panel.querySelector("#prop-width")?.addEventListener("change", (e) => updateField('width', (e.target as HTMLSelectElement).value));
    panel.querySelector("#prop-required")?.addEventListener("change", (e) => updateField('required', (e.target as HTMLInputElement).checked));
    panel.querySelector("#prop-readonly")?.addEventListener("change", (e) => updateField('readonly', (e.target as HTMLInputElement).checked));

    panel.querySelector("#prop-delete")?.addEventListener("click", () => {
      this.schema.fields = this.schema.fields.filter(f => f.id !== this.selectedFieldId);
      this.selectedFieldId = null;
      this.renderCanvas();
      this.renderProperties();
      this.notifyChange();
    });
  }
}

if (!customElements.get("app-form-builder")) {
  customElements.define("app-form-builder", FormBuilder);
}

